import { Dispatch, FC, SetStateAction, Suspense, useEffect, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { useLearnosityBffInitialization } from '../../custom-hooks/useLearnosityBffInitialization';
import { DataLrnSessionBySkill } from '../../custom-hooks/useLrnSession';
import { useAuth0 } from '@auth0/auth0-react';
import { reviewAssessmentRegions } from '../../learnosity-regions/ReviewAssessmentRegions';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import AssessmentCompletedReviewDialog from '../AssessmentCompleted/AssessmentCompletedReviewDialog';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { usePostHog } from '@posthog/react';
import { isAbortError } from '../../utils/fetchCancellation';

interface Props {
  lrnData: DataLrnSessionBySkill | null;
  setOpenLearnosityModal?: Dispatch<SetStateAction<boolean>>;
}

const SkillResourceHeaderLearnosityWrapper: FC<Props> = ({ lrnData, setOpenLearnosityModal }) => {
  const user = useAppSelector(selectUser);
  const { getAccessTokenSilently, logout } = useAuth0();
  const [request, setRequest] = useState<LearnosityRequest | null>(null);
  const [reviewDialogVisible, setReviewDialogVisible] = useState<boolean>(false);
  const posthog = usePostHog();
  const { fetchInitialization, refetchInitialization } = useLearnosityBffInitialization(
    getAccessTokenSilently,
    logout,
    posthog,
  );

  useEffect(() => {
    const controller = new AbortController();
    setRequest(null);
    setReviewDialogVisible(false);
    if (user && user.sub && lrnData) {
      fetchInitialization(
        {
          userId: user.sub,
          state: lrnData.state,
          sessionId: lrnData.sessionId,
          activityId: lrnData.activityId,
          regions: reviewAssessmentRegions,
          title: 'Skill Challenge Review',
          renderingType: 'assess',
          showCorrectAnswers: lrnData.showIncorrectAnswers,
          autoSaveAnswers: false,
          responsiveRegions: false,
          warningOnChange: false,
        },
        { signal: controller.signal },
      )
        .then(data => {
          if (controller.signal.aborted) return;
          setRequest(data);
          setReviewDialogVisible(true);
        })
        .catch(error => {
          if (controller.signal.aborted || isAbortError(error)) return;
          console.log('Error fetching Learnosity bridge');
          setOpenLearnosityModal?.(false);
        });
    }
    return () => controller.abort();
  }, [lrnData?.sessionId, lrnData?.state, lrnData?.activityId, lrnData?.showIncorrectAnswers, user?.sub]);

  return (
    reviewDialogVisible &&
    request && (
      <Suspense fallback={<ThreeDotLoader dark />}>
        <AssessmentCompletedReviewDialog
          visible={reviewDialogVisible}
          setVisible={setReviewDialogVisible}
          request={request}
          refetchInitialization={refetchInitialization}
          setOpenLearnosityModal={setOpenLearnosityModal}
        />
      </Suspense>
    )
  );
};

export default SkillResourceHeaderLearnosityWrapper;
